import React, {useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {SelectButton} from "primereact/selectbutton";
import {CUSTOMER_FIELDS, FIELD_TYPES, MAPPING_POLICIES} from "./engine";
import {ClearableDropdown, DROPDOWN_PT, Icon, PrimeDatePicker, useFormat, useT} from "./host";
import {Field} from "./forms-ui";

// Step 1 «design»: question palette, the form canvas, and the inspector (question settings / form settings).
const GROUPS = [
    ["basic", ["short_text", "long_text", "email", "phone", "number", "amount"]],
    ["choice", ["single_choice", "dropdown", "multi_choice", "yes_no", "rating"]],
    ["time-file", ["date", "datetime", "file", "consent", "hidden"]],
    ["layout", ["heading", "paragraph", "divider"]],
];
export const TYPE_ICONS = {
    short_text: "short_text", long_text: "notes", email: "alternate_email", phone: "call", number: "tag", amount: "payments",
    single_choice: "radio_button_checked", dropdown: "arrow_drop_down_circle", multi_choice: "check_box", yes_no: "help", rating: "star",
    date: "calendar_today", datetime: "schedule", file: "attach_file", consent: "verified_user", hidden: "visibility_off",
    heading: "title", paragraph: "subject", divider: "horizontal_rule",
};
const THEMES = ["zofino", "calm", "dark", "warm"];
const INPUT = "w-full text-xs border-round-lg";
const DROPDOWN = "w-full text-xs border-round-lg";

export function newField(type, fields, t) {
    const used = new Set(fields.map((field) => field.key));
    let index = fields.length + 1;
    while (used.has(`q${index}`)) index += 1;
    const field = {key: `q${index}`, type, label: FIELD_TYPES[type]?.answer ? t("forms.builder.new-question") : t(`forms.field-type.${type}`)};
    if (FIELD_TYPES[type]?.options) field.options = [1, 2].map((n) => ({value: `o${n}`, label: t("forms.builder.option-n", {n})}));
    if (type === "rating") field.scale = 5;
    if (type === "consent") { field.label = t("forms.field-type.consent"); field.consentText = t("forms.builder.consent-default"); }
    if (type === "divider") field.label = "";
    return field;
}

export function Palette({onAdd, disabled}) {
    const t = useT();
    return (
        <aside className="hidden lg:flex flex-column gap-1 w-14rem flex-none surface-card border-left-1 border-200 overflow-auto p-2">
            {GROUPS.map(([group, types]) => (
                <div key={group} className="flex flex-column gap-1">
                    <span className="text-xs text-400 font-bold px-2 pt-2">{t(`forms.palette.${group}`)}</span>
                    {types.map((type) => (
                        <Button key={type} type="button" text disabled={disabled} className="text-xs gap-2 justify-content-start text-700 border-round-lg py-2"
                                icon={<Icon icon={`mi mi:${TYPE_ICONS[type]}`} className="text-base text-primary"/>} label={t(`forms.field-type.${type}`)} onClick={() => onAdd(type)}/>
                    ))}
                </div>
            ))}
        </aside>
    );
}

// a quiet chip, as the owner chips on the customers page (Tag severities are loud brand fills in this theme)
function Chip({label, icon, tone = "surface-100 text-600"}) {
    return (
        <span className={`inline-flex align-items-center gap-1 border-round-2xl px-2 py-1 text-xs ${tone}`}>
            {icon && <Icon icon={`mi mi:${icon}`} className="text-sm"/>}{label}
        </span>
    );
}

function FieldPreview({field}) {
    const t = useT();
    if (field.type === "heading") return <strong className="text-base text-800">{field.label}</strong>;
    if (field.type === "paragraph") return <span className="text-xs text-600 line-height-3 white-space-pre-line">{field.label}</span>;
    if (field.type === "divider") return <div className="border-top-1 border-200 my-2"/>;
    return (
        <div className="flex flex-column gap-1">
            <strong className="text-sm text-800">{field.label || t("forms.builder.no-label")}{field.required && <span className="text-red-500"> *</span>}</strong>
            {field.help && <span className="text-xs text-500">{field.help}</span>}
            {FIELD_TYPES[field.type]?.options && (
                <div className="flex flex-wrap gap-1 mt-1">{(field.options || []).map((option) => <Chip key={option.value} label={option.label}/>)}</div>
            )}
        </div>
    );
}

export function Canvas({draft, selected, onSelect, onChange, readOnly, rulesByField}) {
    const t = useT();
    const fields = draft.fields || [];
    const move = (index, delta) => {
        const next = [...fields];
        const [item] = next.splice(index, 1);
        next.splice(index + delta, 0, item);
        onChange({...draft, fields: next});
    };
    const card = (isSelected) => `border-1 border-round-xl p-3 cursor-pointer flex flex-column gap-2 ${isSelected ? "border-primary surface-50" : "border-200 surface-card hover:border-primary"}`;
    return (
        <div className="flex-1 min-w-0 overflow-auto surface-ground p-3 md:p-4">
            <div className="w-full xl:w-8 mx-auto surface-card border-1 border-200 border-round-2xl border-top-3 border-primary p-3 md:p-4 flex flex-column gap-2">
                <div className={card(selected === "__form")} onClick={() => onSelect("__form")}>
                    <strong className="text-xl text-900">{draft.meta?.title || t("forms.untitled")}</strong>
                    <span className="text-xs text-500 line-height-3 white-space-pre-line">{draft.meta?.description || t("forms.builder.description-placeholder")}</span>
                </div>
                {fields.map((field, index) => (
                    <div key={field.key} className={card(selected === field.key)} onClick={() => onSelect(field.key)}>
                        <FieldPreview field={field}/>
                        <div className="flex flex-wrap align-items-center gap-1">
                            <Chip icon={TYPE_ICONS[field.type]} label={t(`forms.field-type.${field.type}`)}/>
                            {field.mapping?.target && <Chip tone="text-primary border-1 border-primary" icon="person" label={t(`forms.customer-field.${field.mapping.target}`)}/>}
                            {rulesByField[field.key] && <Chip tone="bg-yellow-50 text-yellow-700" icon="account_tree" label={t("forms.builder.conditional")}/>}
                            {field.sensitive && <Chip tone="bg-red-50 text-red-600" icon="lock" label={t("forms.builder.sensitive")}/>}
                            {!readOnly && selected === field.key && (
                                <div className="flex gap-1 mr-auto" onClick={(event) => event.stopPropagation()}>
                                    <Button type="button" text rounded className="p-1" disabled={index === 0} icon={<Icon icon="mi mi:arrow_upward" className="text-base"/>} aria-label={t("forms.builder.move-up")} onClick={() => move(index, -1)}/>
                                    <Button type="button" text rounded className="p-1" disabled={index === fields.length - 1} icon={<Icon icon="mi mi:arrow_downward" className="text-base"/>} aria-label={t("forms.builder.move-down")} onClick={() => move(index, 1)}/>
                                    <Button type="button" text rounded className="p-1" icon={<Icon icon="mi mi:content_copy" className="text-base"/>} aria-label={t("forms.builder.duplicate")} onClick={() => {
                                        const copy = {...JSON.parse(JSON.stringify(field)), key: newField(field.type, fields, t).key, mapping: undefined};
                                        const next = [...fields];
                                        next.splice(index + 1, 0, copy);
                                        onChange({...draft, fields: next});
                                        onSelect(copy.key);
                                    }}/>
                                    <Button type="button" text rounded severity="danger" className="p-1" icon={<Icon icon="mi mi:delete" className="text-base"/>} aria-label={t("forms.builder.delete")} onClick={() => {
                                        onChange({...draft, fields: fields.filter((item) => item.key !== field.key)});
                                        onSelect(null);
                                    }}/>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {fields.length === 0 && <div className="text-center text-xs text-500 p-5">{t("forms.builder.empty")}</div>}
            </div>
        </div>
    );
}

function Toggle({label, hint, checked, onChange, disabled}) {
    return (
        <label className="flex align-items-center justify-content-between gap-2 text-xs text-700">
            <span className="flex flex-column gap-1">{label}{hint && <span className="text-400">{hint}</span>}</span>
            <InputSwitch checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.value)}/>
        </label>
    );
}

function OptionsEditor({field, dimensions, onChange, readOnly}) {
    const t = useT();
    const options = field.options || [];
    const set = (index, patch) => onChange({...field, options: options.map((option, at) => (at === index ? {...option, ...patch} : option))});
    return (
        <Field label={t("forms.builder.options")} hint={dimensions.length ? t("forms.builder.options-scores-hint") : null}>
            <div className="flex flex-column gap-2">
                {options.map((option, index) => (
                    <div key={option.value} className="flex flex-column gap-1 surface-50 border-round-lg p-2">
                        <div className="flex gap-1 align-items-center">
                            <InputText className={INPUT} value={option.label} disabled={readOnly} onChange={(event) => set(index, {label: event.target.value})}/>
                            <Button type="button" text rounded severity="danger" className="p-1 flex-none" icon={<Icon icon="pi pi-times" className="text-sm"/>} disabled={readOnly || options.length <= 1}
                                    aria-label={t("forms.builder.remove-option")} onClick={() => onChange({...field, options: options.filter((_, at) => at !== index)})}/>
                        </div>
                        {dimensions.map((dimension) => (
                            <div key={dimension.key} className="flex align-items-center gap-2 text-xs">
                                <span className="text-500 flex-1">{dimension.label}</span>
                                <InputNumber value={option.scores?.[dimension.key] ?? null} disabled={readOnly} inputClassName="w-4rem text-xs" min={-100} max={100}
                                             onValueChange={(event) => set(index, {scores: {...(option.scores || {}), [dimension.key]: event.value}})}/>
                            </div>
                        ))}
                    </div>
                ))}
                {!readOnly && (
                    <Button type="button" text className="text-xs gap-2 align-self-start" icon={<Icon icon="mi mi:add" className="text-base"/>} label={t("forms.builder.add-option")} onClick={() => {
                        const used = new Set(options.map((option) => option.value));
                        let index = options.length + 1;
                        while (used.has(`o${index}`)) index += 1;
                        onChange({...field, options: [...options, {value: `o${index}`, label: t("forms.builder.option-n", {n: index})}]});
                    }}/>
                )}
            </div>
        </Field>
    );
}

export function FieldInspector({field, draft, onChange, readOnly}) {
    const t = useT();
    const format = useFormat();
    const set = (patch) => onChange({...field, ...patch});
    const kind = FIELD_TYPES[field.type]?.answer;
    const customer = draft.destination?.type === "customer";
    const dimensions = draft.scoreDimensions || [];
    const validation = field.validation || {};
    const setValidation = (patch) => set({validation: {...validation, ...patch}});
    if (!kind) {
        if (field.type === "divider") return <span className="text-xs text-500">{t("forms.builder.divider-help")}</span>;
        return (
            <Field label={t("forms.builder.text")}>
                <InputTextarea className={INPUT} autoResize rows={3} value={field.label || ""} disabled={readOnly} onChange={(event) => set({label: event.target.value})}/>
            </Field>
        );
    }
    return (
        <>
            <Field label={t("forms.builder.question")} required><InputText className={INPUT} value={field.label || ""} disabled={readOnly} onChange={(event) => set({label: event.target.value})}/></Field>
            <Field label={t("forms.builder.help")}><InputText className={INPUT} value={field.help || ""} placeholder={t("forms.optional")} disabled={readOnly} onChange={(event) => set({help: event.target.value})}/></Field>
            {["short_text", "long_text", "email", "phone", "number", "amount", "dropdown"].includes(field.type) && (
                <Field label={t("forms.builder.placeholder")}><InputText className={INPUT} value={field.placeholder || ""} disabled={readOnly} onChange={(event) => set({placeholder: event.target.value})}/></Field>
            )}
            <Field label={t("forms.builder.type")}>
                <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={field.type} disabled={readOnly}
                          options={Object.keys(FIELD_TYPES).filter((type) => FIELD_TYPES[type].answer).map((value) => ({value, label: t(`forms.field-type.${value}`)}))}
                          onChange={(event) => {
                              const next = {...field, type: event.value};
                              if (FIELD_TYPES[event.value].options && !next.options) next.options = [1, 2].map((n) => ({value: `o${n}`, label: t("forms.builder.option-n", {n})}));
                              if (event.value === "rating" && !next.scale) next.scale = 5;
                              onChange(next);
                          }}/>
            </Field>
            {field.type !== "hidden" && <Toggle label={t("forms.builder.required")} checked={field.required} disabled={readOnly} onChange={(value) => set({required: value})}/>}
            <Toggle label={t("forms.builder.sensitive")} hint={t("forms.builder.sensitive-hint")} checked={field.sensitive} disabled={readOnly} onChange={(value) => set({sensitive: value})}/>
            {FIELD_TYPES[field.type]?.options && <OptionsEditor field={field} dimensions={dimensions} onChange={onChange} readOnly={readOnly}/>}
            {field.type === "rating" && (
                <>
                    <Field label={t("forms.builder.scale")}>
                        <SelectButton className="ltr flex text-xs" pt={{button: () => ({className: "flex-1 text-xs py-2"})}} value={field.scale || 5} disabled={readOnly} allowEmpty={false}
                                      options={[3, 5, 7, 10].map((value) => ({value, label: format.number(value)}))} onChange={(event) => set({scale: event.value})}/>
                    </Field>
                    <Field label={t("forms.builder.scale-labels")}>
                        <div className="flex gap-2">
                            <InputText className={INPUT} placeholder={t("forms.builder.scale-low")} value={field.scaleLabels?.[0] || ""} disabled={readOnly} onChange={(event) => set({scaleLabels: [event.target.value, field.scaleLabels?.[1] || ""]})}/>
                            <InputText className={INPUT} placeholder={t("forms.builder.scale-high")} value={field.scaleLabels?.[1] || ""} disabled={readOnly} onChange={(event) => set({scaleLabels: [field.scaleLabels?.[0] || "", event.target.value]})}/>
                        </div>
                    </Field>
                    {dimensions.length > 0 && (
                        <Field label={t("forms.builder.rating-dimensions")}>
                            {dimensions.map((dimension) => (
                                <label key={dimension.key} className="flex align-items-center gap-2 text-xs">
                                    <Checkbox disabled={readOnly} checked={(field.scoreDimensions || []).includes(dimension.key)}
                                              onChange={(event) => set({scoreDimensions: event.checked ? [...(field.scoreDimensions || []), dimension.key] : (field.scoreDimensions || []).filter((key) => key !== dimension.key)})}/>
                                    {dimension.label}
                                </label>
                            ))}
                        </Field>
                    )}
                </>
            )}
            {field.type === "consent" && <Field label={t("forms.builder.consent-text")}><InputTextarea className={INPUT} autoResize rows={2} value={field.consentText || ""} disabled={readOnly} onChange={(event) => set({consentText: event.target.value})}/></Field>}
            {kind === "number" && (
                <Field label={t("forms.builder.range")}>
                    <div className="flex gap-2">
                        <InputNumber placeholder={t("forms.builder.min")} value={validation.min ?? null} disabled={readOnly} onValueChange={(event) => setValidation({min: event.value})} className="flex-1" inputClassName={INPUT}/>
                        <InputNumber placeholder={t("forms.builder.max")} value={validation.max ?? null} disabled={readOnly} onValueChange={(event) => setValidation({max: event.value})} className="flex-1" inputClassName={INPUT}/>
                    </div>
                </Field>
            )}
            {["short_text", "long_text"].includes(field.type) && (
                <Field label={t("forms.builder.max-length")}>
                    <InputNumber value={validation.maxLength ?? null} min={1} max={5000} disabled={readOnly} onValueChange={(event) => setValidation({maxLength: event.value})} inputClassName={INPUT}/>
                </Field>
            )}
            {field.type === "file" && (
                <Field label={t("forms.builder.max-files")} hint={t("forms.builder.file-hint")}>
                    <InputNumber value={validation.maxFiles ?? 1} min={1} max={5} disabled={readOnly} onValueChange={(event) => setValidation({maxFiles: event.value})} inputClassName={INPUT}/>
                </Field>
            )}
            {customer && field.type !== "file" && (
                <div className="surface-50 border-1 border-200 border-round-xl p-3 flex flex-column gap-3">
                    <strong className="text-xs text-800">{t("forms.builder.mapping")}</strong>
                    <Field label={t("forms.builder.mapping-target")}>
                        <ClearableDropdown value={field.mapping?.target || null} disabled={readOnly} placeholder={t("forms.builder.no-mapping")} className="text-xs border-round-lg" panelClassName="rtl" pt={DROPDOWN_PT}
                                           options={CUSTOMER_FIELDS.map((value) => ({value, label: t(`forms.customer-field.${value}`)}))} clearAriaLabel={t("forms.builder.no-mapping")}
                                           onChange={(event) => set({mapping: event.value ? {target: event.value, policy: field.mapping?.policy || "fill_if_empty"} : undefined})}/>
                    </Field>
                    {field.mapping?.target && (
                        <Field label={t("forms.builder.mapping-policy")} hint={["primaryPhone", "primaryEmail"].includes(field.mapping.target) ? t("forms.builder.identity-hint") : null}>
                            <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={field.mapping.policy || "fill_if_empty"} disabled={readOnly}
                                      options={MAPPING_POLICIES.map((value) => ({value, label: t(`forms.policy.${value}`)}))}
                                      onChange={(event) => set({mapping: {...field.mapping, policy: event.value}})}/>
                        </Field>
                    )}
                </div>
            )}
        </>
    );
}

export function FormSettings({draft, onChange, readOnly}) {
    const t = useT();
    const [tab, setTab] = useState("general");
    const meta = draft.meta || {};
    const access = draft.access || {};
    const ending = draft.endings?.default || {type: "message"};
    const setMeta = (patch) => onChange({...draft, meta: {...meta, ...patch}});
    const setAccess = (patch) => onChange({...draft, access: {...access, ...patch}});
    const setEnding = (patch) => onChange({...draft, endings: {...(draft.endings || {}), default: {...ending, ...patch}}});
    return (
        <>
            <SelectButton className="ltr flex text-xs" pt={{button: () => ({className: "flex-1 text-xs py-2"})}} value={tab} allowEmpty={false} onChange={(event) => setTab(event.value)}
                          options={["access", "ending", "general"].map((value) => ({value, label: t(`forms.settings.${value}`)}))}/>
            {tab === "general" && (
                <>
                    <Field label={t("forms.settings.title")} required><InputText className={INPUT} value={meta.title || ""} disabled={readOnly} onChange={(event) => setMeta({title: event.target.value})}/></Field>
                    <Field label={t("forms.settings.description")}><InputTextarea className={INPUT} autoResize rows={3} value={meta.description || ""} disabled={readOnly} onChange={(event) => setMeta({description: event.target.value})}/></Field>
                    <Toggle label={t("forms.settings.progress")} checked={meta.showProgress} disabled={readOnly} onChange={(value) => setMeta({showProgress: value})}/>
                    <Field label={t("forms.settings.theme")}>
                        <SelectButton className="ltr flex text-xs" pt={{button: () => ({className: "flex-1 text-xs py-2"})}} value={meta.theme?.preset || "zofino"} allowEmpty={false} disabled={readOnly}
                                      options={THEMES.map((value) => ({value, label: t(`forms.theme.${value}`)}))} onChange={(event) => setMeta({theme: {...(meta.theme || {}), preset: event.value}})}/>
                    </Field>
                </>
            )}
            {tab === "ending" && (
                <>
                    <Field label={t("forms.settings.after-submit")}>
                        <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={ending.type || "message"} disabled={readOnly} onChange={(event) => setEnding({type: event.value})}
                                  options={["message", "page", "redirect"].map((value) => ({value, label: t(`forms.ending.type.${value}`)}))}/>
                    </Field>
                    <Field label={t("forms.settings.ending-title")}><InputText className={INPUT} value={ending.title || ""} placeholder={t("forms.ending.default-title")} disabled={readOnly} onChange={(event) => setEnding({title: event.target.value})}/></Field>
                    <Field label={t("forms.settings.ending-message")}><InputTextarea className={INPUT} autoResize rows={3} value={ending.message || ""} placeholder={t("forms.ending.default-message")} disabled={readOnly} onChange={(event) => setEnding({message: event.target.value})}/></Field>
                    {ending.type === "redirect" && (
                        <>
                            <Field label={t("forms.settings.redirect-url")} hint={t("forms.settings.https-only")}><InputText dir="ltr" className={INPUT} value={ending.url || ""} placeholder="https://" disabled={readOnly} onChange={(event) => setEnding({url: event.target.value})}/></Field>
                            <label className="flex align-items-center gap-2 text-xs"><Checkbox checked={Boolean(ending.newTab)} disabled={readOnly} onChange={(event) => setEnding({newTab: event.checked})}/>{t("forms.settings.new-tab")}</label>
                        </>
                    )}
                    {ending.type === "page" && (
                        <>
                            <Field label={t("forms.settings.button-label")}><InputText className={INPUT} value={ending.buttonLabel || ""} disabled={readOnly} onChange={(event) => setEnding({buttonLabel: event.target.value})}/></Field>
                            <Field label={t("forms.settings.button-url")} hint={t("forms.settings.https-only")}><InputText dir="ltr" className={INPUT} placeholder="https://" value={ending.buttonUrl || ""} disabled={readOnly} onChange={(event) => setEnding({buttonUrl: event.target.value})}/></Field>
                        </>
                    )}
                </>
            )}
            {tab === "access" && (
                <>
                    <Field label={t("forms.settings.who")}>
                        <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={access.mode || "public"} disabled={readOnly} onChange={(event) => setAccess({mode: event.value})}
                                  options={["public", "internal", "both"].map((value) => ({value, label: t(`forms.access.${value}`)}))}/>
                    </Field>
                    <Field label={t("forms.settings.max-submissions")} hint={t("forms.settings.max-submissions-hint")}>
                        <InputNumber value={access.maxSubmissions ?? null} min={1} placeholder={t("forms.settings.unlimited")} disabled={readOnly} onValueChange={(event) => setAccess({maxSubmissions: event.value})} inputClassName={INPUT}/>
                    </Field>
                    <Field label={t("forms.settings.expires")}>
                        <div className="flex align-items-center gap-1">
                            <div className="flex-1">
                                <PrimeDatePicker inputClassName="w-full" placeholder={t("forms.settings.no-expiry")} value={access.expiresAt ? Math.floor(new Date(access.expiresAt).getTime() / 1000) : null}
                                                 onChange={(event) => { if (readOnly) return; const day = new Date(event.value * 1000); day.setHours(23, 59, 59, 0); setAccess({expiresAt: day.toISOString()}); }}/>
                            </div>
                            <Button type="button" text severity="danger" className="p-1 flex-none" icon={<Icon icon="pi pi-times" className="text-sm"/>} disabled={readOnly || !access.expiresAt}
                                    aria-label={t("forms.settings.clear-expiry")} onClick={() => setAccess({expiresAt: null})}/>
                        </div>
                    </Field>
                </>
            )}
        </>
    );
}
